import React, {useCallback} from "react";
import {observer} from "mobx-react-lite";
import {Alert, Button, Form, Modal, Table} from "react-bootstrap";
import {Upload} from "react-bootstrap-icons";
import BulkStatusImportModalStore from "../../../../stores/backoffice/modals/applications/BulkStatusImportModalStore";

const BulkStatusImportModal: React.FC<{
  store: BulkStatusImportModalStore;
  hideModal: () => void;
}> = observer(({store, hideModal}) => {
  const handleClose = useCallback(() => {
    if (store.state !== "posting") {
      hideModal();
    }
  }, []);

  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      store.loadFile(file);
    }
  }, []);

  return <Modal show={true} onHide={handleClose} centered size="lg">
    <Modal.Header closeButton>
      <Modal.Title><Upload className={"mr-2"}/> Import statuses by e-mail</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      {store.error ? <Alert variant={"danger"}>{store.error}</Alert> : null}

      {store.state === "done" && store.report ? (
        <ImportReport store={store}/>
      ) : (
        <>
          <p className={"text-muted"}>
            Upload a CSV with an <code>email</code> column and an optional <code>status</code> column.
            Rows without a status use the default status below. Transitions follow the status workflow;
            ineligible applications are reported and skipped.
            <br/>
            Optional <code>program</code> and <code>direction</code> columns are applied only when the
            target status is <code>approved</code> or <code>validated</code>: they set the educational
            program and the direction of preparation / scientific specialty (matched by name) and lock
            them in the matriculation module. Unmatched values are reported as warnings; the status is
            still changed.
            <br/>
            Column headers from the matriculation export are accepted as aliases:
            <code>Program level</code> (level), <code>Program Title</code> (program) and
            <code>Research area</code> (direction). For programs that share a name across degrees
            (e.g. Life Sciences, Petroleum Engineering), the <code>level</code> column
            (<code>MSc</code> / <code>PhD</code>) lets the right program be picked even when no
            direction is given.
            <br/>
            UTF-8 and Excel (Windows-1251) files are both accepted. Max file size 5&nbsp;MB.
          </p>

          <Form.Group controlId={"bulk-status-import-file"}>
            <input
              type={"file"}
              accept={".csv,text/csv"}
              onChange={handleFile}
              data-testid={"bulk-status-import-file"}
            />
          </Form.Group>

          {store.state !== "init" ? (
            <p className={"mb-2"} data-testid={"bulk-status-import-parsed"}>
              Parsed <strong>{store.rows.length}</strong> e-mails
              ({store.rowsWithStatusCount} with explicit status, {store.parseInvalid.length} invalid rows).
            </p>
          ) : null}

          <Form.Group controlId={"bulk-status-import-default-status"}>
            <Form.Label>Default status</Form.Label>
            <Form.Control
              as={"select"}
              value={store.defaultStatus}
              onChange={e => store.setDefaultStatus(e.target.value)}
              data-testid={"bulk-status-import-default-status"}
            >
              {store.availableStates.map(s => (
                <option key={s.tag} value={s.tag}>{s.name || s.tag}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </>
      )}
    </Modal.Body>

    <Modal.Footer>
      {store.state === "done" ? (
        <Button variant={"secondary"} onClick={handleClose}>Close</Button>
      ) : (
        <>
          <Button variant={"secondary"} onClick={handleClose} disabled={store.state === "posting"}>Cancel</Button>
          <Button
            variant={"primary"}
            onClick={() => store.submit()}
            disabled={!store.canSubmit}
            data-testid={"bulk-status-import-apply"}
          >
            {store.state === "posting" ? "Applying..." : `Apply to ${store.rows.length} e-mails`}
          </Button>
        </>
      )}
    </Modal.Footer>
  </Modal>;
});

// Человекочитаемые причины для warnings/invalid (бэк отдаёт машинные ключи). Fallback — сам ключ.
const REASON_TEXTS: Record<string, string> = {
  requisites_status_not_applicable: "program/direction are applied only on approved/validated — ignored",
  unknown_program: "program not found by name",
  ambiguous_program: "program name matches several programs (MSc/PhD) — add a level or direction column to disambiguate",
  unknown_direction: "direction / scientific specialty not found by name",
  direction_mismatch: "direction does not belong to the imported program",
  ambiguous_prog_title: "matriculation program (1C) is ambiguous — prog_title left unset",
};

const reasonText = (reason: string): string => REASON_TEXTS[reason] ?? reason;

const ImportReport: React.FC<{store: BulkStatusImportModalStore}> = observer(({store}) => {
  if (!store.report) {
    return null;
  }

  const report = store.report;

  return <div data-testid={"bulk-status-import-report"}>
    <Table size={"sm"} bordered className={"mb-3"}>
      <tbody>
        <tr><td>Total rows</td><td data-testid={"report-total"}>{report.summary.total}</td></tr>
        <tr className={"table-success"}><td>Changed</td><td data-testid={"report-changed"}>{report.summary.changed}</td></tr>
        <tr className={"table-warning"}><td>Warnings</td><td data-testid={"report-warnings"}>{report.summary.warnings}</td></tr>
        <tr className={"table-warning"}><td>Skipped</td><td>{report.summary.skipped}</td></tr>
        <tr><td>Not found</td><td>{report.summary.not_found}</td></tr>
        <tr className={"table-danger"}><td>Invalid</td><td>{report.summary.invalid}</td></tr>
      </tbody>
    </Table>

    {report.warnings.length ? (
      <ReportList title={"Warnings"} items={report.warnings.map(w => `${w.email} — ${w.value}: ${reasonText(w.reason)}`)}/>
    ) : null}
    {report.not_found.length ? <ReportList title={"Not found"} items={report.not_found}/> : null}
    {report.skipped.length ? (
      <ReportList title={"Skipped"} items={report.skipped.map(s => {
        const missing = s.missing && s.missing.length ? `: ${s.missing.join(", ")}` : "";
        return `${s.email} — ${s.reason}${missing} (${s.current} → ${s.target})`;
      })}/>
    ) : null}
    {report.invalid.length ? (
      <ReportList title={"Invalid"} items={report.invalid.map(i => `${i.email || i.value} — ${reasonText(i.reason)}`)}/>
    ) : null}
  </div>;
});

const ReportList: React.FC<{title: string; items: string[]}> = ({title, items}) => (
  <details className={"mb-2"}>
    <summary>{title} ({items.length})</summary>
    <ul className={"mt-2 mb-0"}>
      {items.map((it, idx) => <li key={idx}>{it}</li>)}
    </ul>
  </details>
);

export default BulkStatusImportModal;
